import { Add } from '@mui/icons-material';
import { TableCell, Button, Typography, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { SelectItem, SelectOption } from './SelectOption';
import { useTranslation } from 'react-i18next';

interface Props {
  options: SelectOption[];
  onAddItem: (id: string) => void;
}

const AddTextItemCell = ({ options, onAddItem }: Props) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string | undefined>(undefined);

  const handleAddItem = () => {
    // Ensure inputValue is defined before calling onAddItem
    if (inputValue) {
      onAddItem(inputValue);
    }
  };

  useEffect(() => {
    // When options change, set the input value to the first option if available.
    // Otherwise, clear it.
    if (options.length > 0) {
      setInputValue(options[0].id.toString());
    } else {
      setInputValue(undefined);
    }
  }, [options]);

  if (options.length <= 0)
    return (
      <TableCell
        colSpan={4}
        sx={{
          p: 2,
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            {t('common.no-options')}
          </Typography>
        </Box>
      </TableCell>
    );

  return (
    <TableCell
      colSpan={4}
      sx={{
        p: 2,
        width: '100%',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          width: '100%',
          flexDirection: { xs: 'column', sm: 'row' },
          '& > *': {
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: '250px' },
          },
        }}
      >
        <SelectItem
          value={inputValue}
          options={options}
          onChange={setInputValue}
        />
        <Button
          sx={{
            px: 2,
            py: 1,
            width: 'auto',
            flex: '0 0 auto',
          }}
          onClick={handleAddItem}
          variant="contained"
          color="primary"
          startIcon={<Add />}
          size="medium"
          // Disable the button if no option is selected
          disabled={!inputValue}
        >
          {t('common.add')}
        </Button>
      </Box>
    </TableCell>
  );
};

export default AddTextItemCell;
